import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import {
  getUsersAdmin,
  createUserAdmin,
  updateUserAdmin,
  deleteUserAdmin,
  getOrganizationsAdmin,
  getTeamsAdmin
} from '../services/WebCloudFunctionsService';
import { User, Organization, Team } from '../types/admin';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  User as UserIcon,
  RefreshCw,
  CheckCircle,
  XCircle,
  Crown,
  Zap,
  Target,
  Users as UsersIcon
} from 'lucide-react';

// Using shared User type from ../types/admin

interface UserFormData {
  email: string;
  displayName: string;
  role: 'super_admin' | 'org_admin' | 'team_admin' | 'team_lead' | 'member';
  organizationId: string;
  organizationName: string;
  teamId: string;
  teamName: string;
  isActive: boolean;
}

const Users: React.FC = () => {
  const { theme } = useTheme();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  // Check if we're in debug mode
  const isDebugMode = process.env.REACT_APP_DEBUG === 'true' || process.env.REACT_APP_DEBUG === '1';
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [organizationFilter, setOrganizationFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [alphabetFilter, setAlphabetFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    displayName: '',
    role: 'member',
    organizationId: '',
    organizationName: '',
    teamId: '',
    teamName: '',
    isActive: true,
  });

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);

  useEffect(() => {
    fetchUsers();
    fetchOrganizationsAndTeams();
  }, []);

  const fetchOrganizationsAndTeams = async () => {
    try {
      console.log('🔄 Fetching organizations and teams');

      const [orgsData, teamsData] = await Promise.all([
        getOrganizationsAdmin(),
        getTeamsAdmin()
      ]);

      console.log('✅ Organizations and teams fetched:', { orgsData, teamsData });

      // Ensure data is an array
      const organizationsArray: Organization[] = Array.isArray(orgsData) ? orgsData : [];
      const teamsArray: Team[] = Array.isArray(teamsData) ? teamsData : [];

      setOrganizations(organizationsArray);
      setTeams(teamsArray);
    } catch (err: any) {
      console.error('❌ Error fetching organizations and teams:', err);
      // Don't set error here as it's not critical for the main functionality
    }
  };

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter, organizationFilter, statusFilter, alphabetFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      if (isDebugMode) {
        console.log('🔄 Fetching users with filters:', {
          role: roleFilter !== 'all' ? roleFilter : undefined,
          organizationId: organizationFilter !== 'all' ? organizationFilter : undefined,
          isActive: statusFilter !== 'all' ? statusFilter === 'active' : undefined,
          searchTerm: searchTerm || undefined
        });
      } else {
        console.log('🔄 Fetching users with filters: [filter details hidden - set REACT_APP_DEBUG=true to show]');
      }

      const filters = {
        role: roleFilter !== 'all' ? roleFilter : undefined,
        organizationId: organizationFilter !== 'all' ? organizationFilter : undefined,
        isActive: statusFilter !== 'all' ? statusFilter === 'active' : undefined,
        searchTerm: searchTerm || undefined
      };

      const usersData = await getUsersAdmin(filters);
      if (isDebugMode) {
        console.log('✅ Users fetched successfully:', usersData);
      } else {
        console.log('✅ Users fetched successfully: [user data hidden - set REACT_APP_DEBUG=true to show]');
      }

      // Debug: Log the raw response
      console.log('🔍 Raw usersData response:', usersData);
      console.log('🔍 Type of usersData:', typeof usersData);
      console.log('🔍 Is array:', Array.isArray(usersData));
      console.log('🔍 Length:', usersData?.length);

      // Ensure usersData is an array and properly typed
      let usersArray: User[] = Array.isArray(usersData) ? usersData as User[] : [];
      console.log('🔍 Processed usersArray:', usersArray);
      console.log('🔍 UsersArray length:', usersArray.length);

      // TEMPORARY: If no users found, show sample data for development
      if (usersArray.length === 0) {
        console.log('⚠️ No users found, showing sample data for development');
        usersArray = [
          {
            id: 'sample1',
            email: 'svpasumarthy@gmail.com',
            displayName: 'srivishnu pasumarthy',
            role: 'member',
            organizationId: 'individual',
            organizationName: 'Individual User',
            teamId: 'general',
            teamName: 'General',
            authProvider: 'google',
            joinedDate: '2025-09-04T15:32:54.072Z',
            lastLogin: '2025-09-04T15:32:54.072Z',
            isActive: true,
            totalPoints: 0,
            streak: 0,
            enrolledChallenges: []
          },
          {
            id: 'sample2',
            email: 'vish.pasumarthy@gmail.com',
            displayName: 'Vish Pasumarthy',
            role: 'member',
            organizationId: 'individual',
            organizationName: 'Individual User',
            teamId: 'general',
            teamName: 'General',
            authProvider: 'google',
            joinedDate: '2025-09-08T01:11:09.059Z',
            lastLogin: '2025-09-08T01:11:09.059Z',
            isActive: true,
            totalPoints: 0,
            streak: 0,
            enrolledChallenges: []
          },
          {
            id: 'sample3',
            email: 'Vish.Pasumarthy@UHhospitals.org',
            displayName: 'Pasumarthy, Vish',
            role: 'super_admin',
            organizationId: 'uh',
            organizationName: 'University Hospitals',
            teamId: 'uh_main',
            teamName: 'UH Main',
            authProvider: 'azure',
            joinedDate: '2025-09-05T01:41:00.568Z',
            lastLogin: '2025-10-03T18:37:27.360Z',
            isActive: true,
            totalPoints: 150,
            streak: 5,
            enrolledChallenges: [
              {
                challengeId: 'challenge1',
                challengeTitle: 'Daily Steps Challenge',
                status: 'active',
                progress: 75,
                currentValue: 7500,
                targetValue: 10000,
                startedAt: '2025-09-01T00:00:00Z',
                lastUpdated: '2025-10-03T18:37:27Z',
                pointsEarned: 75
              }
            ]
          }
        ];
      }

      setUsers(usersArray);
      setFilteredUsers(usersArray);
    } catch (err: any) {
      console.error('❌ Error fetching users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.organizationName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Alphabet filter
    if (alphabetFilter !== 'all') {
      filtered = filtered.filter(user =>
        user.displayName.toLowerCase().startsWith(alphabetFilter.toLowerCase())
      );
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // Organization filter
    if (organizationFilter !== 'all') {
      filtered = filtered.filter(user => user.organizationId === organizationFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      filtered = filtered.filter(user => user.isActive === isActive);
    }

    setFilteredUsers(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleCreateUser = async () => {
    try {
      setLoading(true);
      setError(null);

      if (isDebugMode) {
        console.log('🔄 Creating user:', formData);
      } else {
        console.log('🔄 Creating user: [user data hidden - set REACT_APP_DEBUG=true to show]');
      }

      const userData = {
        email: formData.email,
        displayName: formData.displayName,
        role: formData.role,
        organizationId: formData.organizationId || undefined,
        teamId: formData.teamId || undefined,
        isActive: formData.isActive,
      };

      await createUserAdmin(userData);
      console.log('✅ User created successfully');

      // Refresh the users list
      await fetchUsers();
      setShowCreateModal(false);
      resetForm();
    } catch (err: any) {
      console.error('❌ Error creating user:', err);
      setError(err.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Updating user:', { userId: selectedUser.id, updates: formData });

      const updates = {
        displayName: formData.displayName,
        role: formData.role,
        organizationId: formData.organizationId || undefined,
        teamId: formData.teamId || undefined,
        isActive: formData.isActive,
      };

      await updateUserAdmin(selectedUser.id, updates);
      console.log('✅ User updated successfully');

      // Refresh the users list
      await fetchUsers();
      setShowEditModal(false);
      resetForm();
    } catch (err: any) {
      console.error('❌ Error updating user:', err);
      setError(err.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Deleting user:', selectedUser.id);

      await deleteUserAdmin(selectedUser.id);
      console.log('✅ User deleted successfully');

      // Refresh the users list
      await fetchUsers();
      setShowDeleteModal(false);
    } catch (err: any) {
      console.error('❌ Error deleting user:', err);
      setError(err.message || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      displayName: '',
      role: 'member',
      organizationId: '',
      organizationName: '',
      teamId: '',
      teamName: '',
      isActive: true,
    });
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      displayName: user.displayName,
      role: user.role,
      organizationId: user.organizationId || '',
      organizationName: user.organizationName || '',
      teamId: user.teamId || '',
      teamName: user.teamName || '',
      isActive: user.isActive,
    });
    setShowEditModal(true);
  };

  const getTeamsForOrganization = (orgId: string) => {
    return teams.filter(team => team.organizationId === orgId);
  };

  const handleOrganizationChange = (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    setFormData(prev => ({
      ...prev,
      organizationId: orgId,
      organizationName: org?.name || '',
      teamId: '', // Reset team when organization changes
      teamName: '',
    }));
  };

  const handleTeamChange = (teamId: string) => {
    const team = teams.find(t => t.id === teamId);
    setFormData(prev => ({
      ...prev,
      teamId: teamId,
      teamName: team?.name || '',
    }));
  };

  const openDeleteModal = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'super_admin':
        return <Crown size={16} />;
      case 'org_admin':
        return <Zap size={16} />;
      case 'team_lead':
        return <Target size={16} />;
      default:
        return <UserIcon size={16} />;
    }
  };

  const getRoleBadge = (role: string) => {
    const roleColors = {
      super_admin: { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' },
      org_admin: { bg: '#dbeafe', text: '#1e40af', border: '#3b82f6' },
      team_lead: { bg: '#dcfce7', text: '#166534', border: '#22c55e' },
      member: { bg: '#f3f4f6', text: '#374151', border: '#9ca3af' },
    };

    const colors = roleColors[role as keyof typeof roleColors] || roleColors.member;

    return (
      <span style={{
        backgroundColor: colors.bg,
        color: colors.text,
        border: `1px solid ${colors.border}`,
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
      }}>
        {getRoleIcon(role)}
        {role.replace('_', ' ')}
      </span>
    );
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span style={{
        backgroundColor: isActive ? '#dcfce7' : '#fee2e2',
        color: isActive ? '#166534' : '#dc2626',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
      }}>
        {isActive ? <CheckCircle size={12} /> : <XCircle size={12} />}
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  const getAuthProviderIcon = (provider: string) => {
    switch (provider) {
      case 'google':
        return '🔍';
      case 'azure':
        return '☁️';
      default:
        return '📧';
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        backgroundColor: theme.colors.background,
        color: theme.colors.text
      }}>
        <div style={{ textAlign: 'center' }}>
          <RefreshCw className="animate-spin" size={32} />
          <p style={{ marginTop: '16px', color: theme.colors.textSecondary }}>
            Loading users...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-blue-900/20 dark:to-indigo-900/20 p-6">
      <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-3">
              👥 Users & Participants
            </h1>
            <p className="text-gray-600 dark:text-gray-300 text-base">
              Manage users, their participation in challenges, and search by challenge enrollment
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 focus:ring-4 focus:ring-blue-500/30 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          >
            <Plus size={16} />
            <span>Add User</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-6 mb-6">
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '16px',
        }}>
          {/* Search */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '8px',
              color: theme.colors.text
            }}>
              Search Users
            </label>
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: theme.colors.textSecondary
              }} />
              <input
                type="text"
                placeholder="Search by name, email, organization, or challenge titles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 40px',
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: theme.colors.background,
                  color: theme.colors.text,
                }}
              />
            </div>
          </div>

          {/* Alphabet Filter */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '8px',
              color: theme.colors.text
            }}>
              Quick Filter
            </label>
            <div style={{
              display: 'flex',
              gap: '4px',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <button
                onClick={() => setAlphabetFilter('all')}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${alphabetFilter === 'all' ? theme.colors.primary : theme.colors.border}`,
                  backgroundColor: alphabetFilter === 'all' ? theme.colors.primary : theme.colors.background,
                  color: alphabetFilter === 'all' ? theme.colors.primaryText : theme.colors.text,
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                All
              </button>
              {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'].map(letter => (
                <button
                  key={letter}
                  onClick={() => setAlphabetFilter(letter)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: `1px solid ${alphabetFilter === letter ? theme.colors.primary : theme.colors.border}`,
                    backgroundColor: alphabetFilter === letter ? theme.colors.primary : theme.colors.background,
                    color: alphabetFilter === letter ? theme.colors.primaryText : theme.colors.text,
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    minWidth: '32px',
                  }}
                >
                  {letter}
                </button>
              ))}
            </div>
          </div>

          {/* Role Filter */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '8px',
              color: theme.colors.text
            }}>
              Role
            </label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: theme.colors.background,
                color: theme.colors.text,
              }}
            >
              <option value="all">All Roles</option>
              <option value="super_admin">Super Admin</option>
              <option value="org_admin">Organization Admin</option>
              <option value="team_lead">Team Lead</option>
              <option value="member">Member</option>
            </select>
          </div>

          {/* Organization Filter */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '8px',
              color: theme.colors.text
            }}>
              Organization
            </label>
            <select
              value={organizationFilter}
              onChange={(e) => setOrganizationFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: theme.colors.background,
                color: theme.colors.text,
              }}
            >
              <option value="all">All Organizations</option>
              {organizations && organizations.length > 0 && organizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '8px',
              color: theme.colors.text
            }}>
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: `1px solid ${theme.colors.border}`,
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: theme.colors.background,
                color: theme.colors.text,
              }}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span style={{
            fontSize: '14px',
            color: theme.colors.textSecondary
          }}>
            {filteredUsers.length} users found
          </span>

          <button
            onClick={fetchUsers}
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${theme.colors.border}`,
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              color: theme.colors.text,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 overflow-hidden">
        <div style={{
          overflowX: 'auto',
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
          }}>
            <thead style={{
              backgroundColor: theme.colors.background,
              borderBottom: `1px solid ${theme.colors.border}`,
            }}>
              <tr>
                <th style={{
                  padding: '16px',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: theme.colors.text,
                }}>
                  User
                </th>
                <th style={{
                  padding: '16px',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: theme.colors.text,
                }}>
                  Role
                </th>
                <th style={{
                  padding: '16px',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: theme.colors.text,
                }}>
                  Organization
                </th>
                <th style={{
                  padding: '16px',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: theme.colors.text,
                }}>
                  Status
                </th>
                <th style={{
                  padding: '16px',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: theme.colors.text,
                }}>
                  Points
                </th>
                <th style={{
                  padding: '16px',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: theme.colors.text,
                }}>
                  Challenges
                </th>
                <th style={{
                  padding: '16px',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: theme.colors.text,
                }}>
                  Last Login
                </th>
                <th style={{
                  padding: '16px',
                  textAlign: 'center',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: theme.colors.text,
                }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.map((user) => (
                <tr key={user.id} style={{
                  borderBottom: `1px solid ${theme.colors.border}`,
                }}>
                  <td style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: theme.colors.primary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.colors.primaryText,
                        fontSize: '16px',
                        fontWeight: '600',
                      }}>
                        {user.displayName.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: theme.colors.text,
                          marginBottom: '4px',
                        }}>
                          {user.displayName}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: theme.colors.textSecondary,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}>
                          {getAuthProviderIcon(user.authProvider)}
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    {getRoleBadge(user.role)}
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div>
                      <div style={{
                        fontSize: '14px',
                        color: theme.colors.text,
                        marginBottom: '2px',
                      }}>
                        {user.organizationName || 'No Organization'}
                      </div>
                      {user.teamName && (
                        <div style={{
                          fontSize: '12px',
                          color: theme.colors.textSecondary,
                        }}>
                          {user.teamName}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    {getStatusBadge(user.isActive)}
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: theme.colors.text,
                      }}>
                        {user.totalPoints.toLocaleString()} pts
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: theme.colors.textSecondary,
                      }}>
                        {user.streak} day streak
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div style={{ maxWidth: '200px' }}>
                      {user.enrolledChallenges && user.enrolledChallenges.length > 0 ? (
                        <>
                          {user.enrolledChallenges.slice(0, 2).map((challenge, index) => (
                            <div key={challenge.challengeId} style={{
                              fontSize: '12px',
                              color: theme.colors.text,
                              marginBottom: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}>
                              <span style={{
                                display: 'inline-block',
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: challenge.status === 'completed' ? '#10B981' :
                                  challenge.status === 'in_progress' ? '#3B82F6' : '#EF4444',
                              }}></span>
                              <span style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '120px'
                              }}>
                                {challenge.challengeTitle}
                              </span>
                              <span style={{ color: theme.colors.textSecondary }}>
                                ({Math.round(challenge.progress)}%)
                              </span>
                            </div>
                          ))}
                          {user.enrolledChallenges.length > 2 && (
                            <div style={{
                              fontSize: '10px',
                              color: theme.colors.textSecondary,
                            }}>
                              +{user.enrolledChallenges.length - 2} more
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={{
                          fontSize: '12px',
                          color: theme.colors.textSecondary,
                          fontStyle: 'italic',
                        }}>
                          No challenges
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div style={{
                      fontSize: '12px',
                      color: theme.colors.textSecondary,
                    }}>
                      {user.lastLogin
                        ? new Date(user.lastLogin).toLocaleDateString()
                        : 'Never'
                      }
                    </div>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '8px',
                    }}>
                      <button
                        onClick={() => openEditModal(user)}
                        style={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          padding: '6px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          color: theme.colors.primary,
                        }}
                        title="Edit User"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => openDeleteModal(user)}
                        style={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          padding: '6px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          color: theme.colors.error,
                        }}
                        title="Delete User"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 0',
            borderTop: `1px solid ${theme.colors.border}`,
          }}>
            <div style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
            }}>
              Showing {startIndex + 1} to {Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} users
            </div>

            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
            }}>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.colors.border}`,
                  backgroundColor: currentPage === 1 ? theme.colors.background : theme.colors.surface,
                  color: currentPage === 1 ? theme.colors.textSecondary : theme.colors.text,
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                }}
              >
                Previous
              </button>

              {/* Page numbers */}
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                // Show first page, last page, current page, and pages around current page
                const showPage = page === 1 || page === totalPages ||
                  (page >= currentPage - 2 && page <= currentPage + 2);

                if (!showPage) {
                  // Show ellipsis
                  if (page === 2 && currentPage > 4) {
                    return (
                      <span key={`ellipsis-${page}`} style={{
                        padding: '8px 4px',
                        color: theme.colors.textSecondary,
                        fontSize: '14px',
                      }}>
                        ...
                      </span>
                    );
                  }
                  if (page === totalPages - 1 && currentPage < totalPages - 3) {
                    return (
                      <span key={`ellipsis-${page}`} style={{
                        padding: '8px 4px',
                        color: theme.colors.textSecondary,
                        fontSize: '14px',
                      }}>
                        ...
                      </span>
                    );
                  }
                  return null;
                }

                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: `1px solid ${currentPage === page ? theme.colors.primary : theme.colors.border}`,
                      backgroundColor: currentPage === page ? theme.colors.primary : theme.colors.surface,
                      color: currentPage === page ? theme.colors.primaryText : theme.colors.text,
                      cursor: 'pointer',
                      fontSize: '14px',
                      minWidth: '40px',
                    }}
                  >
                    {page}
                  </button>
                );
              })}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${theme.colors.border}`,
                  backgroundColor: currentPage === totalPages ? theme.colors.background : theme.colors.surface,
                  color: currentPage === totalPages ? theme.colors.textSecondary : theme.colors.text,
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: theme.colors.surface,
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto',
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              margin: '0 0 20px 0',
              color: theme.colors.text,
            }}>
              Create New User
            </h2>

            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: theme.colors.text
                }}>
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text,
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: theme.colors.text
                }}>
                  Display Name *
                </label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text,
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: theme.colors.text
                }}>
                  Role *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text,
                  }}
                >
                  <option value="member">Member</option>
                  <option value="team_lead">Team Lead</option>
                  <option value="team_admin">Team Admin</option>
                  <option value="org_admin">Organization Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: theme.colors.text
                }}>
                  Organization
                </label>
                <select
                  value={formData.organizationId}
                  onChange={(e) => {
                    const org = organizations.find(o => o.id === e.target.value);
                    setFormData({
                      ...formData,
                      organizationId: e.target.value,
                      organizationName: org?.name || ''
                    });
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text,
                  }}
                >
                  <option value="">No Organization</option>
                  {organizations && organizations.length > 0 && organizations.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
              </div>

              {formData.organizationId && (
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    color: theme.colors.text
                  }}>
                    Team
                  </label>
                  <select
                    value={formData.teamId}
                    onChange={(e) => {
                      const team = teams.find(t => t.id === e.target.value);
                      setFormData({
                        ...formData,
                        teamId: e.target.value,
                        teamName: team?.name || ''
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      backgroundColor: theme.colors.background,
                      color: theme.colors.text,
                    }}
                  >
                    <option value="">No Team</option>
                    {teams
                      .filter(team => team.organizationId === formData.organizationId)
                      .map(team => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))
                    }
                  </select>
                </div>
              )}

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                <label htmlFor="isActive" style={{
                  fontSize: '14px',
                  color: theme.colors.text,
                }}>
                  Active User
                </label>
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: '24px',
            }}>
              <button
                onClick={handleCreateUser}
                style={{
                  backgroundColor: theme.colors.primary,
                  color: theme.colors.primaryText,
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Create User
              </button>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: `1px solid ${theme.colors.border}`,
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: theme.colors.text,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: theme.colors.surface,
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto',
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              margin: '0 0 20px 0',
              color: theme.colors.text,
            }}>
              Edit User: {selectedUser.displayName}
            </h2>

            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: theme.colors.text
                }}>
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text,
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: theme.colors.text
                }}>
                  Display Name
                </label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text,
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: theme.colors.text
                }}>
                  Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text,
                  }}
                >
                  <option value="member">Member</option>
                  <option value="team_lead">Team Lead</option>
                  <option value="team_admin">Team Admin</option>
                  <option value="org_admin">Organization Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: theme.colors.text
                }}>
                  Organization
                </label>
                <select
                  value={formData.organizationId}
                  onChange={(e) => {
                    const org = organizations.find(o => o.id === e.target.value);
                    setFormData({
                      ...formData,
                      organizationId: e.target.value,
                      organizationName: org?.name || ''
                    });
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: theme.colors.background,
                    color: theme.colors.text,
                  }}
                >
                  <option value="">No Organization</option>
                  {organizations && organizations.length > 0 && organizations.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
              </div>

              {formData.organizationId && (
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    color: theme.colors.text
                  }}>
                    Team
                  </label>
                  <select
                    value={formData.teamId}
                    onChange={(e) => {
                      const team = teams.find(t => t.id === e.target.value);
                      setFormData({
                        ...formData,
                        teamId: e.target.value,
                        teamName: team?.name || ''
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      backgroundColor: theme.colors.background,
                      color: theme.colors.text,
                    }}
                  >
                    <option value="">No Team</option>
                    {teams
                      .filter(team => team.organizationId === formData.organizationId)
                      .map(team => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))
                    }
                  </select>
                </div>
              )}

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <input
                  type="checkbox"
                  id="isActiveEdit"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                <label htmlFor="isActiveEdit" style={{
                  fontSize: '14px',
                  color: theme.colors.text,
                }}>
                  Active User
                </label>
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: '24px',
            }}>
              <button
                onClick={handleUpdateUser}
                style={{
                  backgroundColor: theme.colors.primary,
                  color: theme.colors.primaryText,
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Update User
              </button>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: `1px solid ${theme.colors.border}`,
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: theme.colors.text,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: theme.colors.surface,
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '400px',
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              margin: '0 0 16px 0',
              color: theme.colors.text,
            }}>
              Delete User
            </h2>

            <p style={{
              fontSize: '14px',
              color: theme.colors.textSecondary,
              marginBottom: '24px',
              lineHeight: '1.5',
            }}>
              Are you sure you want to delete <strong>{selectedUser.displayName}</strong>?
              This action cannot be undone.
            </p>

            <div style={{
              display: 'flex',
              gap: '12px',
            }}>
              <button
                onClick={handleDeleteUser}
                style={{
                  backgroundColor: theme.colors.error,
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Delete User
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: `1px solid ${theme.colors.border}`,
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: theme.colors.text,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="fixed top-5 right-5 bg-red-500 text-white px-5 py-3 rounded-lg text-sm z-50 shadow-xl">
          {error}
        </div>
      )}
      </div>
    </div>
  );
};

export default Users; 